import { CheckCircle2, LoaderCircle, LogIn, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { env } from "../../lib/env";
import { supabase } from "../../lib/supabase/client";
import { useAuth } from "./AuthContext";
import { getOAuthRedirectUrl } from "./oauthRouting";

type InvitationDetails = {
  invitation_email: string;
  invitation_role: string;
  invitation_expires_at: string;
  invitation_status: string;
};

function formatRole(value: string) {
  return value.replaceAll("_", " ");
}

function invitationMessage(value: string) {
  if (value.includes("invitation_expired"))
    return "This invitation has expired. Ask a Foundation administrator to resend it.";
  if (value.includes("invitation_already_used"))
    return "This invitation has already been accepted.";
  if (value.includes("invitation_email_mismatch"))
    return "Sign in with the Google account that uses the invited email address.";
  return "This invitation is invalid or is no longer available. Ask a Foundation administrator for a new link.";
}

export function AcceptInvitationPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token")?.trim() ?? "", [searchParams]);
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "accepting" | "complete" | "error">(
    "loading",
  );
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!supabase || !token) {
      setMessage("This invitation link is incomplete.");
      setStatus("error");
      return;
    }
    void supabase.rpc("verify_invitation_token", { p_token: token }).then(({ data, error }) => {
      const invitation = (data?.[0] ?? null) as InvitationDetails | null;
      if (error || !invitation) {
        setMessage(invitationMessage(error?.message ?? "invitation_invalid"));
        setStatus("error");
        return;
      }
      setDetails(invitation);
      setStatus("ready");
    });
  }, [token]);

  const signIn = async () => {
    if (!supabase) return;
    const returnTo = `/accept-invitation?token=${encodeURIComponent(token)}`;
    window.sessionStorage.setItem("guiding-stars:auth-return-to", returnTo);
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: getOAuthRedirectUrl(env.VITE_SITE_URL) },
    });
  };

  const accept = async () => {
    if (!supabase || !token) return;
    setStatus("accepting");
    const { error } = await supabase.rpc("accept_invitation", { raw_token: token });
    if (error) {
      setMessage(invitationMessage(error.message));
      setStatus("error");
      return;
    }
    await auth.refreshProfile();
    setStatus("complete");
    window.setTimeout(() => navigate("/atlas", { replace: true }), 900);
  };

  return (
    <section className="auth-page">
      <div className="auth-stars" aria-hidden="true" />
      <div className="auth-card invitation-card">
        {status === "loading" || status === "accepting" ? (
          <>
            <LoaderCircle className="spin auth-mark" aria-hidden="true" />
            <h1>{status === "accepting" ? "Joining My Atlas…" : "Checking your invitation…"}</h1>
          </>
        ) : status === "complete" ? (
          <>
            <CheckCircle2 className="auth-mark" aria-hidden="true" />
            <h1>Your invitation is accepted.</h1>
            <p>Opening your private Atlas now.</p>
          </>
        ) : status === "error" ? (
          <>
            <ShieldCheck className="auth-mark" aria-hidden="true" />
            <h1>We couldn’t accept this invitation.</h1>
            <p className="form-error" role="alert">
              {message}
            </p>
          </>
        ) : (
          <>
            <p className="eyebrow">My Atlas invitation</p>
            <h1>You’re invited to join Guiding Stars.</h1>
            <p>
              This invitation is for <strong>{details?.invitation_email}</strong> as a{" "}
              <strong>{formatRole(details?.invitation_role ?? "")}</strong> and expires on{" "}
              <strong>{new Date(details?.invitation_expires_at ?? "").toLocaleDateString()}</strong>
              .
            </p>
            {auth.user ? (
              <button className="google-button" type="button" onClick={() => void accept()}>
                <CheckCircle2 aria-hidden="true" /> Accept invitation
              </button>
            ) : (
              <button className="google-button" type="button" onClick={() => void signIn()}>
                <LogIn aria-hidden="true" /> Continue with Google to accept
              </button>
            )}
            <div className="auth-assurance">
              <ShieldCheck aria-hidden="true" />
              <span>Use the Google account for {details?.invitation_email}.</span>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
